import { hasPermission } from 'core/auth/rbac'
import { ConfirmDialog } from 'core/components/ui/ConfirmDialog'
import { FilterBar } from 'core/components/ui/FilterBar'
import { PageHeader } from 'core/components/ui/PageHeader'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import { useState } from 'react'
import { User } from '../../domain/entities/User'
import { ResetPasswordModal } from '../components/ResetPasswordModal'
import { UserFormModal } from '../components/UserFormModal'
import { UserTable } from '../components/UserTable'
import { useUsers } from '../hooks/useUsers'

export function UserManagementPage() {
  const { user } = useAuth()
  const canManage = hasPermission(user, 'manage_users')

  const {
    users,
    loading,
    error,
    search,
    setSearch,
    role,
    setRole,
    page,
    setPage,
    totalPages,
    totalItems,
    createUser,
    updateUser,
    toggleStatus,
    resetFilters,
  } = useUsers(10)

  // Modals state
  const [formOpen, setFormOpen] = useState(false)
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [userToToggle, setUserToToggle] = useState<User | null>(null)
  const [toggling, setToggling] = useState(false)

  const handleAddClick = () => {
    setSelectedUser(null)
    setFormOpen(true)
  }

  const handleEditClick = (targetUser: User) => {
    setSelectedUser(targetUser)
    setFormOpen(true)
  }

  const handleResetPasswordClick = (targetUser: User) => {
    setSelectedUser(targetUser)
    setResetModalOpen(true)
  }

  const handleFormSubmit = async (
    data: Omit<User, 'id' | 'keycloakId' | 'status' | 'createdAt' | 'updatedAt'> & { password?: string },
  ) => {
    if (selectedUser) {
      await updateUser(selectedUser.id, data)
    } else {
      await createUser(data)
    }
  }

  const handleToggleClick = (targetUser: User) => {
    setUserToToggle(targetUser)
    setConfirmOpen(true)
  }

  const handleToggleConfirm = async () => {
    if (!userToToggle) return
    setToggling(true)
    try {
      await toggleStatus(userToToggle.id, userToToggle.status)
      setConfirmOpen(false)
      setUserToToggle(null)
    } catch (err) {
      // Error handled by custom hook state
    } finally {
      setToggling(false)
    }
  }

  const isFilterActive = !!(search || role)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="User Management"
        description="Oversee internal staff accounts, external retailers, FPOs, and village collection point coordinators."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Users' }]}
        actions={
          canManage ? (
            <button
              onClick={handleAddClick}
              className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn shadow transition-colors flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              Add User Profile
            </button>
          ) : undefined
        }
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search phone or email..."
        searchValue={search}
        onSearchChange={setSearch}
        showReset={isFilterActive}
        onReset={resetFilters}
      >
        {/* Role Filter */}
        <select
          value={role}
          onChange={(e) => {
            setRole(e.target.value)
            setPage(1)
          }}
          className="px-3 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none"
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="cxo">CXO</option>
          <option value="supervisor">Supervisor</option>
          <option value="finance">Finance</option>
          <option value="account_manager">Account Manager</option>
          <option value="customer_support">Customer Support</option>
          <option value="fpo">FPO</option>
          <option value="vlcc">VLCC</option>
          <option value="retailer">Retailer</option>
        </select>
      </FilterBar>

      {/* Data Table */}
      <UserTable
        users={users}
        loading={loading}
        onEdit={handleEditClick}
        onToggleStatus={handleToggleClick}
        onResetPassword={handleResetPasswordClick}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={10}
        canManage={canManage}
      />

      {/* Add / Edit Form Modal */}
      <UserFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        user={selectedUser}
      />

      <ResetPasswordModal isOpen={resetModalOpen} onClose={() => setResetModalOpen(false)} user={selectedUser} />

      {/* Status Toggle Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleToggleConfirm}
        title={userToToggle?.status === 'active' ? 'Deactivate User Profile' : 'Activate User Profile'}
        message={`Are you sure you want to change status of user ${userToToggle?.id} to ${
          userToToggle?.status === 'active' ? 'inactive' : 'active'
        }?`}
        confirmText={userToToggle?.status === 'active' ? 'Deactivate' : 'Activate'}
        tone={userToToggle?.status === 'active' ? 'danger' : 'primary'}
        loading={toggling}
      />
    </div>
  )
}
